import { DestroyRef, Injectable, effect, inject, untracked } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivityTrackerService } from './activity-tracker.service';
import { ProfileService } from '../../shared/profiles/services/profile.service';

/** A visit in progress. Duration counts only the time the tab was visible. */
interface Visit {
  startedAt: Date;
  visibleMs: number;
  visibleSince: number | null;
}

interface PageVisit extends Visit {
  path: string;
  template: string;
  title: string;
}

interface ProfileVisit extends Visit {
  key: string;
  record: { type: string; id: string | number | null; name: string | null };
  details: Record<string, unknown>;
}

/** Pages that are never counted: sign-in screens and headless PDF renders. */
export function isUntrackedUrl(url: string): boolean {
  const path = stripQuery(url);
  return path === '/login' || path.startsWith('/auth/') || /[?&]prerender=1(&|#|$)/.test(url);
}

/** The address without query string or fragment — those can carry search
 *  text or client names, which the log must not hold. */
export function stripQuery(url: string): string {
  return url.split(/[?#]/)[0] || '/';
}

/** The matched route pattern, e.g. "/broker/cases/:id", so visits to different
 *  records of the same page group together. */
export function routeTemplate(root: ActivatedRouteSnapshot): string {
  const parts: string[] = [];
  let node: ActivatedRouteSnapshot | null = root;
  while (node) {
    const p = node.routeConfig?.path;
    if (p) parts.push(p);
    node = node.firstChild;
  }
  return '/' + parts.join('/');
}

/**
 * Usage tracking — page visits and product-profile opens (Beacon decision D7).
 *
 * One event per visit, sent when the visit ends (next navigation, profile
 * closed or switched, tab closed) and dated from when it began, with the
 * visible time spent. A query-only change on the same page is not a new visit.
 *
 * Started from App (like ImpersonationAuditService), not an app initializer,
 * so it is listening before the first navigation completes but after the
 * session has been adopted from an open tab.
 */
@Injectable({ providedIn: 'root' })
export class UsageTrackerService {
  private readonly router = inject(Router);
  private readonly titles = inject(TitleStrategy);
  private readonly activity = inject(ActivityTrackerService);
  private readonly profile = inject(ProfileService);

  private page: PageVisit | null = null;
  private profileVisit: ProfileVisit | null = null;

  constructor() {
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntilDestroyed(),
    ).subscribe(e => this.onNavigation(e.urlAfterRedirects, this.router.routerState.snapshot));

    effect(() => {
      const open = this.profile.isOpen();
      const ctx = this.profile.context();
      const rider = this.profile.riderContext();
      const name = this.profile.headerProductName();
      untracked(() => this.onProfile(open, ctx, rider, name));
    });

    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      const onVisibility = () => this.onVisibility(document.visibilityState === 'visible');
      const onHide = () => this.onPageHide();
      const onShow = (e: PageTransitionEvent) => { if (e.persisted) this.onPageRestored(); };
      document.addEventListener('visibilitychange', onVisibility);
      window.addEventListener('pagehide', onHide);
      window.addEventListener('pageshow', onShow);
      inject(DestroyRef).onDestroy(() => {
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('pagehide', onHide);
        window.removeEventListener('pageshow', onShow);
      });
    }
  }

  // ── Pages ──────────────────────────────────────────────────────────────────

  private onNavigation(url: string, state: RouterStateSnapshot): void {
    if (isUntrackedUrl(url)) {
      this.endPage();
      return;
    }
    const path = stripQuery(url);
    if (this.page?.path === path) return;
    this.endPage();
    this.page = {
      ...startVisit(),
      path,
      template: routeTemplate(state.root),
      title: this.titles.buildTitle(state) ?? '',
    };
  }

  private endPage(): void {
    const v = this.page;
    this.page = null;
    if (!v) return;
    this.activity.track('page.viewed', {
      occurredAt: v.startedAt,
      details: { path: v.path, template: v.template, title: v.title || undefined, durationMs: elapsed(v) },
    });
  }

  // ── Profiles ───────────────────────────────────────────────────────────────

  private onProfile(
    open: boolean,
    ctx: ReturnType<ProfileService['context']>,
    rider: ReturnType<ProfileService['riderContext']>,
    name: string,
  ): void {
    const next = !open ? null
      : rider ? {
          key: `rider:${rider.companyId}:${rider.riderName}`,
          record: { type: 'rider', id: null, name: rider.riderName || null },
          details: { riderType: rider.riderType, company: rider.companyName || undefined, source: rider.source },
        }
      : ctx ? {
          key: `product:${ctx.productType}:${ctx.productId}`,
          record: { type: 'product', id: ctx.productId, name: name?.trim() || null },
          details: { productType: ctx.productType, source: ctx.source, tab: ctx.scrollToTab },
        }
      : null;

    if (next && this.profileVisit?.key === next.key) {
      // Same profile — the name arrives once its data loads.
      if (!this.profileVisit.record.name && next.record.name) this.profileVisit.record.name = next.record.name;
      return;
    }
    this.endProfile();
    if (next) this.profileVisit = { ...startVisit(), ...next };
  }

  private endProfile(): void {
    const v = this.profileVisit;
    this.profileVisit = null;
    if (!v) return;
    this.activity.track('profile.viewed', {
      occurredAt: v.startedAt,
      record: v.record,
      details: { ...v.details, durationMs: elapsed(v) },
    });
  }

  // ── Tab lifecycle ──────────────────────────────────────────────────────────

  private onVisibility(visible: boolean): void {
    for (const v of [this.page, this.profileVisit]) {
      if (!v) continue;
      if (visible) {
        if (v.visibleSince == null) v.visibleSince = Date.now();
      } else if (v.visibleSince != null) {
        v.visibleMs += Date.now() - v.visibleSince;
        v.visibleSince = null;
      }
    }
  }

  /** The tab is closing: end both visits and push them out with keepalive.
   *  The tracker's own pagehide handler has already run by now. */
  private onPageHide(): void {
    const hadVisit = !!(this.page || this.profileVisit);
    this.endProfile();
    const current = this.page;
    this.endPage();
    // Keep where we were, so a back/forward-cache restore can resume it.
    if (current) this.restorePath = { path: current.path, template: current.template, title: current.title };
    if (hadVisit) this.activity.flushOnUnload();
  }

  private restorePath: { path: string; template: string; title: string } | null = null;

  /** Back/forward cache brought the page back without a navigation. */
  private onPageRestored(): void {
    if (this.page || !this.restorePath) return;
    this.page = { ...startVisit(), ...this.restorePath };
    this.restorePath = null;
  }
}

function startVisit(): Visit {
  const visible = typeof document === 'undefined' || document.visibilityState !== 'hidden';
  return { startedAt: new Date(), visibleMs: 0, visibleSince: visible ? Date.now() : null };
}

function elapsed(v: Visit): number {
  return v.visibleMs + (v.visibleSince != null ? Date.now() - v.visibleSince : 0);
}
